import { Mutation } from "@apollo/client/react/components";
import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Hover } from "react-powerplug";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import AcceptGroupRequest from "~/lib/mutations/AcceptGroupRequest";
import DeclineGroupRequest from "~/lib/mutations/DeclineGroupRequest";
import DismissActionCenter from "~/lib/mutations/DismissActionCenter";
import {
  IAcceptGroupRequestMutation,
  IAcceptGroupRequestMutationVariables,
} from "~/lib/mutations/__generated__/AcceptGroupRequest.generated";
import {
  IDeclineGroupRequestMutation,
  IDeclineGroupRequestMutationVariables,
} from "~/lib/mutations/__generated__/DeclineGroupRequest.generated";
import {
  IDismissActionCenterMutation,
  IDismissActionCenterMutationVariables,
} from "~/lib/mutations/__generated__/DismissActionCenter.generated";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { IGovernmentUser, IGroup, IGroupRequest, IPendingUser, IVendorUser } from "~/types/types";
import CheckIcon from "~/components/icons/CheckIcon";
import CloseIcon from "~/components/icons/CloseIcon";
import { Margin } from "~/components/spacings";

type Inviter =
  | Pick<IVendorUser, "__typename" | "fullName" | "slug">
  | Pick<IGovernmentUser, "__typename" | "fullName" | "slug">
  | Pick<IPendingUser, "__typename" | "fullName" | "slug">;

interface IGovernmentGroupRequestProps {
  action: Pick<IGroupRequest, "_id" | "createdAt"> & { group: Pick<IGroup, "_id" | "name" | "logo" | "slug"> } & {
    inviter: Inviter;
  };
  entityType: "vendor" | "government";
}

function GroupRequestAction({ action, entityType }: IGovernmentGroupRequestProps) {
  const intl = useIntl();
  const [joinedGroup, setJoinedGroup] = useState(false);
  const [declinedGroup, setDeclinedGroup] = useState(false);
  const router = useRouter();

  return (
    <Mutation<IDismissActionCenterMutation, IDismissActionCenterMutationVariables> mutation={DismissActionCenter}>
      {(dismissActionCenter) => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            padding: 12,
            backgroundColor: palette.white,
            ":hover": {
              backgroundColor: palette.washGray,
            },
          }}
        >
          <ProfilePicture size={46} image={action.group.logo} name={action.group.name} />

          <Margin ml={12} mr="auto">
            <p
              css={{
                fontSize: 14,
                color: palette.mediumGray,
                margin: 0,
                lineHeight: "18px",
              }}
            >
              <Link passHref={true} href="/[userSlug]/[[...tab]]" as={`/@${action.inviter.slug}`} legacyBehavior={true}>
                <a
                  css={{
                    color: palette.primary,
                    fontWeight: 600,
                    ":hover": {
                      textDecoration: "underline !important",
                    },
                  }}
                >
                  {action.inviter.fullName}
                </a>
              </Link>{" "}
              {entityType === "vendor" && (
                <FormattedMessage
                  defaultMessage="invited your vendor to join the group {groupName}"
                  id="lGjb4l"
                  values={{
                    groupName: action.group.name,
                  }}
                />
              )}
              {entityType === "government" && (
                <FormattedMessage
                  defaultMessage="invited your government to join the group {groupName}"
                  id="1AZSUN"
                  values={{
                    groupName: action.group.name,
                  }}
                />
              )}{" "}
              <span
                css={{
                  color: palette.sealBlue,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                <IntlRelativeTime value={action.createdAt} />
              </span>
            </p>
          </Margin>

          <div
            css={{
              display: "flex",
              width: 170,
              minWidth: 170,
              justifyContent: "flex-end",
            }}
          >
            {joinedGroup && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.greenBlue,
                  }}
                >
                  <CheckIcon />
                  <Margin ml={6}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Joined" id="l8wanU" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {declinedGroup && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.red,
                  }}
                >
                  <CloseIcon fill={palette.red} variation="thick" width={15} height={15} />
                  <Margin ml={8}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Declined" id="uRYIb9" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {!joinedGroup && !declinedGroup && (
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  alignItems: "center",
                  gridColumnGap: 12,
                }}
              >
                <Mutation<IAcceptGroupRequestMutation, IAcceptGroupRequestMutationVariables>
                  mutation={AcceptGroupRequest}
                  refetchQueries={refetchQueries(["Selfie", "Government", "Group"])}
                  onCompleted={() => {
                    router.push("/groups/[groupSlug]", `/groups/${action.group.slug}`);
                  }}
                >
                  {(acceptGroupRequest) => (
                    <Hover>
                      {({ bind, hovered }) => (
                        <Button
                          theme={hovered ? "primary" : "secondary"}
                          size="xsmall"
                          color={palette.innovatorBlue}
                          style={{
                            opacity: 1,
                            backgroundColor: hovered ? palette.innovatorBlue : palette.white,
                            width: 100,
                          }}
                          {...bind}
                          onClick={() => {
                            acceptGroupRequest({
                              variables: {
                                requestId: action._id,
                              },
                            });
                            dismissActionCenter();
                            setJoinedGroup(true);
                          }}
                        >
                          <span
                            css={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FormattedMessage defaultMessage="Join Group" id="8hYalv" />
                          </span>
                        </Button>
                      )}
                    </Hover>
                  )}
                </Mutation>

                <Mutation<IDeclineGroupRequestMutation, IDeclineGroupRequestMutationVariables>
                  mutation={DeclineGroupRequest}
                  refetchQueries={refetchQueries(["Group"])}
                >
                  {(declineGroupRequest) => (
                    <span
                      css={{
                        cursor: "pointer",
                        fontSize: 12,
                        color: palette.red,
                        ":hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        const message = intl.formatMessage(
                          {
                            defaultMessage: "Are you sure you want to decline the invite to {groupName}?",
                            id: "V4iue9",
                          },
                          {
                            groupName: action.group.name,
                          },
                        );

                        if (window.confirm(message)) {
                          declineGroupRequest({
                            variables: {
                              requestId: action._id,
                            },
                          });
                          dismissActionCenter();
                          setDeclinedGroup(true);
                        }
                      }}
                    >
                      <FormattedMessage defaultMessage="Decline" id="pvtgR2" />
                    </span>
                  )}
                </Mutation>
              </div>
            )}
          </div>
        </div>
      )}
    </Mutation>
  );
}

export default GroupRequestAction;
